import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';

import useAuth from '@/hooks/useAuth';

import { createOrderV2Client } from '../api/client';
import { orderV2QueryKeys } from '../api/queryKeys';
import {
  BillingAccountV2,
  CustomersDirectoryV2,
  DirectoryAccount,
  DirectoryCustomer,
} from '../api/types';

// Wraps the `/customers_directory` aggregator. Provides:
// - `directory`: the raw nested response
// - `billingAccounts`: a flattened, denormalized list compatible with the
//   wizard's existing `BillingAccountV2` shape (id, name, customer_id,
//   active, rate_type, markup_percent, enable_surge_rate, tax_type,
//   require_background_checks)
// - `customers`: the customer-level list for tenant-admin client search
// - `findCustomerByAccountId(id)`: locates the parent customer for a
//   selected billing account
// - `refresh()`: re-fetches with `?fresh=1` so a freshly created customer
//   becomes visible without waiting for the 5-min Redis TTL
const flattenAccount = (
  customer: DirectoryCustomer,
  account: DirectoryAccount,
): BillingAccountV2 => ({
  id: account.id,
  name: account.name ?? null,
  customer_id: customer.id,
  active: account.active,
  rate_type: account.rate_type,
  markup_percent: Number(account.markup_percent ?? 0),
  enable_surge_rate: customer.enable_surge_rate === true,
  tax_type: customer.job_tax_type ?? null,
  require_background_checks: customer.require_background_checks === true,
});

const useCustomersDirectory = () => {
  const { currentAgency } = useAuth();
  const agencyId = currentAgency?.id ? Number(currentAgency.id) : null;
  const enabled = agencyId != null;
  const queryClient = useQueryClient();

  // `?fresh=1` is a per-call flag — the cached result and the
  // cache-busted result share the same logical key. We track it on a ref
  // and read it inside `queryFn` so the next refetch sends the flag
  // exactly once, then reverts.
  const freshRef = useRef(false);

  const query = useQuery<CustomersDirectoryV2 | null>({
    queryKey: enabled
      ? orderV2QueryKeys.customersDirectory(agencyId)
      : ['orders_v2', 'customers_directory', 'disabled'],
    queryFn: async () => {
      const fresh = freshRef.current;
      freshRef.current = false;
      const data = await createOrderV2Client.listCustomersDirectory({
        agencyId: agencyId as number,
        fresh,
      });
      return data ?? null;
    },
    enabled,
  });

  const directory = query.data ?? null;

  const billingAccounts = useMemo<BillingAccountV2[]>(() => {
    if (!directory) return [];
    return directory.customers.flatMap((customer) =>
      customer.accounts.map((account) => flattenAccount(customer, account)),
    );
  }, [directory]);

  const customers = directory?.customers ?? [];

  const findCustomerByAccountId = useCallback(
    (accountId: number | null) => {
      if (accountId == null || !directory) return null;
      return (
        directory.customers.find((customer) =>
          customer.accounts.some((account) => account.id === accountId),
        ) ?? null
      );
    },
    [directory],
  );

  const refresh = useCallback(async () => {
    if (!enabled) return;
    freshRef.current = true;
    await queryClient.refetchQueries({
      queryKey: orderV2QueryKeys.customersDirectory(agencyId),
    });
  }, [enabled, agencyId, queryClient]);

  return {
    directory,
    billingAccounts,
    customers,
    findCustomerByAccountId,
    loading: query.isFetching,
    error: query.error
      ? (query.error as Error).message || 'Failed to load directory'
      : null,
    refresh,
  };
};

export default useCustomersDirectory;
